import React, { useEffect, useRef, useState } from 'react'
import { ChatStoryScript } from '../../data/howItWorks'
import { useReducedMotion } from '../../hooks/useReducedMotion'

const TYPE_CHAR_MS = 38
const BEFORE_SEND_MS = 280
const THEIR_TYPING_MS = 1300
const BETWEEN_MESSAGES_MS = 650
const START_DELAY_MS = 500

interface ChatStoryProps {
  chat: ChatStoryScript
  label: string
  isActive: boolean
}

const wait = (ms: number, signal: { cancelled: boolean }) =>
  new Promise<void>((resolve, reject) => {
    window.setTimeout(() => (signal.cancelled ? reject(new Error('cancelled')) : resolve()), ms)
  })

/**
 * Step 3's phone: the app's chat screen rebuilt in HTML so it can play like live messaging — the user's messages
 * type out in the input bar and send, the other person's replies arrive after a typing indicator. Restarts each
 * time the step becomes active; shows the finished conversation when reduced motion is on.
 */
const ChatStory: React.FC<ChatStoryProps> = ({ chat, label, isActive }) => {
  const reducedMotion = useReducedMotion()
  const [shown, setShown] = useState(0)
  const [draft, setDraft] = useState('')
  const [theyAreTyping, setTheyAreTyping] = useState(false)
  const threadRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isActive) return
    if (reducedMotion) {
      setShown(chat.messages.length)
      return
    }
    const signal = { cancelled: false }
    setShown(0)
    setDraft('')
    setTheyAreTyping(false)

    const play = async () => {
      await wait(START_DELAY_MS, signal)
      for (let i = 0; i < chat.messages.length; i++) {
        const message = chat.messages[i]
        if (message.from === 'me') {
          // By code point, not UTF-16 unit — slicing mid-emoji shows a broken "�" while typing.
          const chars = Array.from(message.text)
          for (let c = 1; c <= chars.length; c++) {
            setDraft(chars.slice(0, c).join(''))
            await wait(TYPE_CHAR_MS, signal)
          }
          await wait(BEFORE_SEND_MS, signal)
          setDraft('')
        } else {
          setTheyAreTyping(true)
          await wait(THEIR_TYPING_MS, signal)
          setTheyAreTyping(false)
        }
        setShown(i + 1)
        await wait(BETWEEN_MESSAGES_MS, signal)
      }
    }
    play().catch(() => undefined)
    return () => {
      signal.cancelled = true
    }
  }, [isActive, reducedMotion, chat])

  // Keep the newest message in view once the thread outgrows the screen.
  useEffect(() => {
    const thread = threadRef.current
    if (!thread) return
    thread.scrollTo({ top: thread.scrollHeight, behavior: reducedMotion ? 'auto' : 'smooth' })
  }, [shown, theyAreTyping, reducedMotion])

  return (
    <div aria-label={label} role="img" className="v2-phone">
      <div className="v2-phone__screen v2-chat">
        <div className="v2-chat__status">
          <span>9:41</span>
          <i className="v2-chat__island" />
        </div>
        <div className="v2-chat__header">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M19 12H5M11 6l-6 6 6 6" />
          </svg>
          <span className="v2-chat__name">{chat.contactName}</span>
          <span className="v2-chat__more" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
        </div>

        <div ref={threadRef} className="v2-chat__thread">
          <div className="v2-chat__date">
            <span>Today</span>
          </div>
          {chat.messages.slice(0, shown).map((message, index) =>
            message.from === 'me' ? (
              <div key={index} className="v2-chat__row v2-chat__row--me">
                <div className="v2-chat__bubble v2-chat__bubble--me">
                  {message.text}
                  <small>{message.time}</small>
                </div>
              </div>
            ) : (
              <div key={index} className="v2-chat__row v2-chat__row--them">
                <img className="v2-chat__avatar" src={chat.contactPhoto} alt="" />
                <div className="v2-chat__bubble v2-chat__bubble--them">
                  {message.text}
                  <small>{message.time}</small>
                </div>
              </div>
            ),
          )}
          {theyAreTyping && (
            <div className="v2-chat__row v2-chat__row--them">
              <img className="v2-chat__avatar" src={chat.contactPhoto} alt="" />
              <div className="v2-chat__bubble v2-chat__bubble--them v2-chat__typing" aria-hidden="true">
                <i />
                <i />
                <i />
              </div>
            </div>
          )}
        </div>

        <div className="v2-chat__composer">
          <div className="v2-chat__input" data-typing={draft.length > 0}>
            {draft || 'Message'}
          </div>
          <svg className="v2-chat__send" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M3 20.5 21 12 3 3.5l2.6 8.5L3 20.5Z" />
          </svg>
        </div>
      </div>
    </div>
  )
}

export default ChatStory
